// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// modes.ts — the three modes. A mode has to change how the game PLAYS, not just a number, and the
// test each of these had to pass is: does a strategy that wins here LOSE in the other two?
//
//   • standing — the full game. Finite ore in eight seams, unlimited presses, kill the Forge or hold
//     the most tonnage at 6:00. The dominant thought is "can I afford this fight?", because a won
//     fight still leaves you permanently poorer.
//   • lastbatch — the Forge has SIX presses for the whole match and then it is dead metal, but ore is
//     abundant. Money stops being the constraint and presses become it, so the question inverts from
//     "can I afford this" to "is this the right shape". You start with six units already deployed and
//     they fight for the entire match, so the survivors are all at 30% with no armour left.
//   • theline — Forges are invulnerable and there is one objective: a neutral Foundry in the middle
//     paying 5 ore/s to whoever holds it, first to bank 600 wins. No macro game and nowhere to hide;
//     first contact at 0:40 and continuous after. Wrecks pile up in one corridor, which walls the
//     Arbalests out of the objective until somebody spends twenty seconds salvaging the ring away.
//
// The three cannot collapse into each other: standing↔lastbatch differ on what is scarce and on
// whether reinforcement exists at all; standing↔theline differ on the win condition, the map's
// centre and whether a base can be attacked. tests/balance.test.ts measures all three separately,
// because a mode is a distinct product surface for balance exactly as it is for layout.
//
// The HOST's pick travels frozen inside rematch.ts's round start, and the whole battle derives from
// (mode, seed, command log) alone, so a mode can never desync. modeOf() guards an id off the wire
// with Object.hasOwn — a bare `MODES[id] ||` lets 'constructor' through as a Mode of undefined
// fields, which would reach the map generator as NaN.

/** The simulation runs at 20 Hz. Every duration in this file is in ticks. */
export const TICK_HZ = 20;
export const secs = (s: number): number => Math.round(s * TICK_HZ);

export interface Mode {
  id: string;
  name: string;
  /** Alive + in production may not exceed this. */
  unitCap: number;
  /** Total presses the Forge will ever accept. 0 = unlimited. */
  buildBudget: number;
  /** Ore each side starts with. */
  startReserve: number;
  /** Ore in each pre-claimed home seam, and the milli-ore/tick it pays while held uncontested. */
  homeOre: number;
  homeRate: number;
  /** Ore in each neutral central seam, and its rate. Ignored when `foundry` is set. */
  centralOre: number;
  centralRate: number;
  /** theline: the four central seams become one 4x2 Foundry with these numbers. */
  foundry: boolean;
  foundryOre: number;
  foundryRate: number;
  /** theline: bank this much Foundry ore to win outright. */
  foundryTarget: number;
  /** Can a Forge be destroyed? False in theline, where it only produces. */
  forgeKill: boolean;
  /** Rust begins here and doubles every 30 s — the anti-stalemate clock. */
  rustAt: number;
  /** Adjudication. The match cannot run past this. */
  hardEnd: number;
  /** Six pre-deployed units per side (lastbatch), instead of the usual two scouts. */
  deployed: boolean;
  blurb: string;
  /** One line for the how-to-play card, which has to state what differs per mode. */
  rule: string;
}

export const MODES: Record<string, Mode> = {
  standing: {
    id: 'standing',
    name: 'Standing',
    unitCap: 12,
    buildBudget: 0,
    startReserve: 130,
    homeOre: 260,
    homeRate: 75,
    centralOre: 200,
    centralRate: 100,
    foundry: false,
    foundryOre: 0,
    foundryRate: 0,
    foundryTarget: 0,
    forgeKill: true,
    rustAt: secs(270),
    hardEnd: secs(360),
    deployed: false,
    blurb:
      'The whole game. Two thousand ore in the ground and nobody is making more, so every fight you win leaves you permanently poorer and every unit you lose becomes the other side’s payroll. Seams run dry around three minutes; after that the only economy left is picking over the dead.',
    rule: 'Break the enemy Forge, or hold the most tonnage at 6:00.',
  },
  /**
   * SIX PRESSES, AND THE NUMBER MATTERS. Ore is 2.5× the rate and 1.6× the quantity, so the reserve
   * is never the thing stopping you — it is the press counter, which is a completely different kind
   * of scarcity. It also means the same bodies fight for four straight minutes, which is the only
   * mode where spalling has time to strip every unit on the board to armour 0.
   */
  lastbatch: {
    id: 'lastbatch',
    name: 'Last Batch',
    unitCap: 12,
    buildBudget: 6,
    startReserve: 200,
    homeOre: 416,
    homeRate: 188,
    centralOre: 320,
    centralRate: 250,
    foundry: false,
    foundryOre: 0,
    foundryRate: 0,
    foundryTarget: 0,
    forgeKill: true,
    rustAt: secs(180),
    hardEnd: secs(240),
    deployed: true,
    blurb:
      'Chess with a burning fuse. Six presses for the entire match and then the Forge is dead metal — but ore is everywhere, so money never stops you. You start with six units already out and they are the ones that finish it, at thirty per cent, with nothing left to spall.',
    rule: 'Six builds for the whole match. Ore is abundant; presses are not.',
  },
  theline: {
    id: 'theline',
    name: 'The Line',
    unitCap: 10,
    buildBudget: 0,
    startReserve: 160,
    homeOre: 160,
    homeRate: 75,
    centralOre: 0,
    centralRate: 0,
    foundry: true,
    foundryOre: 1200,
    foundryRate: 250,
    foundryTarget: 600,
    forgeKill: false,
    rustAt: secs(210),
    hardEnd: secs(300),
    deployed: false,
    blurb:
      'No macro game and nowhere to hide. The Forges cannot be broken and the middle of the map is one eight-tile Foundry paying five ore a second to whoever stands on it. First to bank six hundred wins. Everything dies in the same corridor, and the wall of wrecks it builds is the whole strategic game.',
    rule: 'Bank 600 ore from the Foundry. Forges are invulnerable.',
  },
};

export const MODE_IDS: string[] = ['standing', 'lastbatch', 'theline'];

/**
 * Standing is the default because it is the mode that teaches the game: it is the only one where
 * both halves of the economy — seams while they last, then corpses — are on screen in one match.
 */
export const DEFAULT_MODE = 'standing';

export function modeOf(id: string | null | undefined): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) return MODES[id];
  return MODES[DEFAULT_MODE];
}

/**
 * Rust chip damage per tick, in centi-HP, at match tick `t`. Starts at 2 HP/s and doubles every
 * 30 s. A full Anvil dies to rust alone in about 57 s from the start of the ramp, and a Forge is
 * gone roughly two minutes after that — so a match cannot outlast the clock however passive both
 * players are. Integer throughout: the doubling is a shift, not a pow().
 */
export function rustCenti(mode: Mode, t: number): number {
  if (t < mode.rustAt) return 0;
  const steps = Math.min(6, Math.floor((t - mode.rustAt) / secs(30)));
  // 2 HP/s = 10 centi-HP per tick at 20 Hz.
  return 10 << steps;
}
